import { useState } from 'react';
import { Alert, Modal, Skeleton } from 'antd';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faCheck, faDownload, faFile, faPen, faPlay, faXmark } from '@fortawesome/free-solid-svg-icons';
import {
  Actions,
  Block,
  CallRecording,
  DangerLink,
  DetailModal,
  FactsBlock,
  Field,
  FieldLabel,
  Form,
  FormField,
  GhostBtn,
  List,
  Muted,
  PrimaryBtn,
  TonePill,
  TypeTag
} from '../../components';
import { useDeleteOwner, useDeleteOwnerFile, useOwnerQuery, useUpdateOwner, useUploadOwnerFiles } from '../../queries/owners';
import { portalApi } from '../../services/portalApi';
import { downloadBlob } from '../../utils/csv';
import { adaptCall } from '../../services/callAdapter';
import { tint, tintT } from '../../theme/tokens';
import { formatDate } from '../../utils/datetime';
import { errorText } from '../../utils/errors';
import { FILE_TONES, lastContactLabel, monthValue, openRequestsLabel, statusOf, termOf } from './constants';
import { FileDrop } from './OwnerFormModal';
import { formatPhone } from '../../utils/callLog';
import { CallHead, Chip, EditActions, FileRow, ItemCard, LinkRow } from './styled';

const EDIT_FIELDS = [
  { key: 'name', label: 'Owner name' },
  { key: 'phone', label: 'Phone', type: 'tel' },
  { key: 'email', label: 'Email', type: 'email' },
  { key: 'address', label: 'Address' },
  { key: 'website', label: 'Website' },
  { key: 'started', label: 'Date started', type: 'month' },
  { key: 'ended', label: 'Date ended', type: 'month' }
];

const editsOf = (o) => ({
  name: o.name || '',
  phone: o.phone || '',
  email: o.email || '',
  address: o.address || '',
  website: o.website || '',
  started: monthValue(o.startedAt),
  ended: monthValue(o.endedAt)
});

const monthToDate = (v) => (v ? `${v}-01` : null);

function OwnerEdit({ owner, onDone }) {
  const [edits, setEdits] = useState(() => editsOf(owner));
  const update = useUpdateOwner();
  const valid = Boolean(edits.name.trim());

  const save = (e) => {
    e.preventDefault();
    if (!valid) return;
    const { started, ended, ...values } = edits;
    update.mutate({ _id: owner._id, ...values, name: values.name.trim(), startedAt: monthToDate(started), endedAt: monthToDate(ended) });
    onDone();
  };

  return (
    <Block as="form" onSubmit={save} aria-label="Edit owner">
      <Form>
        {EDIT_FIELDS.map((f) => (
          <FormField key={f.key}>
            <FieldLabel htmlFor={`edit-${f.key}`}>{f.label}</FieldLabel>
            <Field
              id={`edit-${f.key}`}
              type={f.type || 'text'}
              value={edits[f.key]}
              onChange={(e) => setEdits((s) => ({ ...s, [f.key]: e.target.value }))}
            />
          </FormField>
        ))}
      </Form>
      <EditActions>
        <PrimaryBtn type="submit" disabled={!valid}>
          <FontAwesomeIcon icon={faCheck} />
          Save changes
        </PrimaryBtn>
        <GhostBtn type="button" onClick={onDone}>
          Cancel
        </GhostBtn>
      </EditActions>
    </Block>
  );
}

function Properties({ properties }) {
  const navigate = useNavigate();
  return (
    <Block>
      <h3>Properties owned</h3>
      {properties.length ? (
        <List style={{ gap: 6 }}>
          {properties.map((p) => (
            <li key={p._id}>
              <LinkRow type="button" onClick={() => navigate('/properties', { state: { openListingId: p._id } })}>
                <span>{p.name}</span>
                <FontAwesomeIcon icon={faArrowRight} />
              </LinkRow>
            </li>
          ))}
        </List>
      ) : (
        <Muted>No properties linked to this owner.</Muted>
      )}
    </Block>
  );
}

function Contacts({ contacts }) {
  return (
    <Block>
      <h3>Associated contacts</h3>
      {contacts.length ? (
        <List style={{ gap: 8 }}>
          {contacts.map((c) => (
            <ItemCard key={c._id || c.name}>
              <strong>{c.name}</strong>
              {c.role && <small>{c.role}</small>}
              <p>{[c.phone && formatPhone(c.phone), c.email].filter(Boolean).join(' · ')}</p>
            </ItemCard>
          ))}
        </List>
      ) : (
        <Muted>No additional contacts on file.</Muted>
      )}
    </Block>
  );
}

function Files({ ownerId, files, canManage }) {
  const upload = useUploadOwnerFiles();
  const remove = useDeleteOwnerFile();
  const download = (f) =>
    portalApi
      .ownerFile(ownerId, f._id)
      .then((blob) => downloadBlob(f.name, blob))
      .catch((err) => toast.error(errorText(err, 'Could not download this file')));

  return (
    <Block>
      <h3>Files</h3>
      {canManage && <FileDrop onAdd={(added) => upload.mutate({ id: ownerId, files: added })} />}
      {files.length ? (
        <List style={{ gap: 6 }}>
          {files.map((f) => (
            <FileRow key={f._id || f.name}>
              <FontAwesomeIcon icon={faFile} />
              <div>
                <strong>{f.name}</strong>
                <small>Added {formatDate(f.addedAt)}</small>
              </div>
              <TonePill $tone={FILE_TONES[f.type]}>{f.type}</TonePill>
              {f.key && (
                <Chip type="button" style={{ minWidth: 24, minHeight: 24 }} aria-label={`Download ${f.name}`} onClick={() => download(f)}>
                  <FontAwesomeIcon icon={faDownload} />
                </Chip>
              )}
              {canManage && f._id && (
                <Chip
                  type="button"
                  style={{ minWidth: 24, minHeight: 24 }}
                  aria-label={`Remove ${f.name}`}
                  disabled={remove.isPending}
                  onClick={() => remove.mutate({ id: ownerId, fileId: f._id })}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </Chip>
              )}
            </FileRow>
          ))}
        </List>
      ) : (
        <Muted>No files uploaded yet.</Muted>
      )}
    </Block>
  );
}

function CallItem({ call }) {
  const [playing, setPlaying] = useState(false);
  return (
    <ItemCard>
      <CallHead>
        <TypeTag $color={tintT(call.type.color)} $bg={tint(call.type.color)}>
          {call.type.label}
        </TypeTag>
        {call.hasRecording && (
          <button type="button" aria-label={`Play recording from ${call.date}`} onClick={() => setPlaying(true)}>
            <FontAwesomeIcon icon={faPlay} />
            Play
          </button>
        )}
      </CallHead>
      <small style={{ marginTop: 6 }}>{[`${call.date}, ${call.time}`, call.property].filter(Boolean).join(' · ')}</small>
      {call.summary && <p style={{ marginTop: 4 }}>{call.summary}</p>}
      {playing && <CallRecording callId={call.id} />}
    </ItemCard>
  );
}

function Calls({ calls }) {
  return (
    <Block>
      <h3>Related call logs</h3>
      {calls.length ? (
        <List style={{ gap: 8 }}>
          {calls.map((c) => (
            <CallItem key={c._id} call={adaptCall(c)} />
          ))}
        </List>
      ) : (
        <Muted>No calls logged for this owner or their units.</Muted>
      )}
    </Block>
  );
}

export default function OwnerModal({ id, preview, canManage, onClose }) {
  const { data, isLoading, error } = useOwnerQuery(id);
  const [editing, setEditing] = useState(false);
  const remove = useDeleteOwner();
  const owner = data?.owner || preview;

  const confirmDelete = () =>
    Modal.confirm({
      title: `Remove ${owner.name}?`,
      content: 'Their files are deleted and their properties are left without an owner.',
      okText: 'Remove',
      okButtonProps: { danger: true },
      onOk: () => {
        onClose();
        remove.mutate(owner._id);
      }
    });

  return (
    <DetailModal
      title={owner.name}
      subtitle={`Owner ${termOf(owner)}`}
      status={statusOf(owner)}
      actions={
        canManage && !editing ? (
          <button type="button" aria-label="Edit owner" onClick={() => setEditing(true)}>
            <FontAwesomeIcon icon={faPen} />
          </button>
        ) : null
      }
      onClose={onClose}
      footer={
        canManage && !editing ? (
          <Actions>
            <DangerLink type="button" onClick={confirmDelete}>
              Remove owner
            </DangerLink>
          </Actions>
        ) : null
      }
    >
      {error && <Alert type="error" showIcon style={{ margin: 16 }} message={errorText(error, 'Could not load this owner')} />}
      {editing ? (
        <OwnerEdit owner={owner} onDone={() => setEditing(false)} />
      ) : (
        <FactsBlock
          facts={[
            { label: 'Phone', value: owner.phone && formatPhone(owner.phone) },
            { label: 'Email', value: owner.email && <a href={`mailto:${owner.email}`}>{owner.email}</a> },
            { label: 'Address', value: owner.address },
            { label: 'Website', value: owner.website },
            { label: 'Open requests', value: openRequestsLabel(owner.openRequests) },
            { label: 'Last contact', value: lastContactLabel(owner.lastContactAt) }
          ]}
        />
      )}
      <Properties properties={owner.properties || []} />
      <Contacts contacts={owner.contacts || []} />
      <Files ownerId={owner._id} files={owner.files || []} canManage={canManage} />
      {isLoading ? (
        <Block>
          <Skeleton active paragraph={{ rows: 3 }} />
        </Block>
      ) : (
        data && <Calls calls={data.calls || []} />
      )}
    </DetailModal>
  );
}
